/**
 * Reference detection (openable-refs): deterministic, allocation-light scanning of transcript text
 * for things the user will want to open — file paths (absolute, repo-relative, partial, with
 * `:line`, `:line-line` or `#L10-L20`), directories, commit SHAs, branch names, local dev-server
 * URLs and artifact ids. Detection never touches the network; the run's machine resolves the
 * candidates (`run.resolve_refs`) and only resolved ones become links.
 */
/** Resolution cache key: the same file at different lines resolves once. */
export function refKey(r) {
    return `${r.kind}:${r.text}`;
}
/** Extensions a bare filename (no directory) must carry to count as a reference in prose. */
const KNOWN_EXT = new Set(('ts tsx js jsx mjs cjs mts cts json jsonc json5 md mdx markdown txt log csv tsv yml yaml toml ini cfg conf env ' +
    'py pyi rb go rs java kt kts swift m mm h hpp c cc cpp cs fs php pl lua r dart scala ex exs erl clj ' +
    'sh bash zsh fish ps1 bat cmd sql graphql gql proto html htm css scss sass less vue svelte astro ' +
    'xml plist svg png jpg jpeg gif webp avif ico bmp heic mp4 webm mov pdf zip lock gradle cmake mk ' +
    'dockerfile tf hcl nix ipynb wasm snap diff patch').split(' '));
const SEG = String.raw `[\w@+\[\]-][\w.@+\[\]-]*`;
const PREFIX = String.raw `(?:[A-Za-z]:[\\/]|~[\\/]|\.{1,2}[\\/]|[\\/])?`;
const LINE = String.raw `(?::(\d+)(?:(?::\d+)|-(\d+))?|#L(\d+)(?:-L?(\d+))?)?`;
/** Path with a file extension, in prose or tool output. */
const FILE_RE = new RegExp(String.raw `(?<![\w/\\.@~:#-])(${PREFIX}(?:${SEG}[\\/])*${SEG}\.[A-Za-z][A-Za-z0-9]{0,9})${LINE}(?![\w/\\@-])`, 'g');
const URL_RE = /\bhttps?:\/\/[^\s<>`"'{}|\\^]+/g;
const SHA_RE = /(?<![\w/.-])[0-9a-f]{7,40}(?![\w/-])/g;
const ARTIFACT_RE = /\b(?:art|blob)_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/g;
/** Words that make a following hex run a commit rather than an id or hash of something else. */
const SHA_CONTEXT = /\b(?:commit(?:s|ted)?|sha|hash|head|merged?|merge commit|rebased?|onto|cherry-pick(?:ed)?|revert(?:ed|s)?|landed|amend(?:ed)?|fixup|at|in|as|squashed)\b[\s:`(#]*$/i;
const BRANCH_PREFIX = /^(?:feature|feat|fix|bugfix|hotfix|chore|refactor|docs|test|release|mvp|codex|claude|viko|wip|exp|spike)\/[\w./-]+$/;
export function isLocalHost(host) {
    const h = host.toLowerCase().replace(/^\[|\]$/g, '');
    if (h === 'localhost' || h.endsWith('.localhost') || h.endsWith('.local'))
        return true;
    if (h === '::1' || h === '0.0.0.0')
        return true;
    const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (!m)
        return false;
    const [a, b] = [Number(m[1]), Number(m[2])];
    return a === 127 || a === 10 || (a === 192 && b === 168) || (a === 172 && b >= 16 && b <= 31);
}
/** A URL served on the run's machine (dev/preview server) rather than the public web. */
export function isLocalUrl(url) {
    try {
        const u = new URL(url);
        return (u.protocol === 'http:' || u.protocol === 'https:') && isLocalHost(u.hostname);
    }
    catch {
        return false;
    }
}
function trimUrl(raw) {
    let u = raw;
    // Trailing sentence punctuation and unbalanced closing brackets are not part of the URL.
    for (;;) {
        const last = u.at(-1);
        if (!last)
            break;
        if ('.,;:!?\'"'.includes(last))
            u = u.slice(0, -1);
        else if (last === ')' && (u.match(/\(/g)?.length ?? 0) < (u.match(/\)/g)?.length ?? 0))
            u = u.slice(0, -1);
        else if (last === ']' && !u.includes('['))
            u = u.slice(0, -1);
        else
            break;
    }
    return u;
}
function lineOf(m) {
    const a = m[2] ?? m[4];
    const b = m[3] ?? m[5];
    if (!a)
        return {};
    const line = Number(a);
    const lineEnd = b ? Number(b) : undefined;
    return { line, ...(lineEnd && lineEnd >= line ? { lineEnd } : {}) };
}
function extOf(p) {
    const base = p.split(/[\\/]/).pop() ?? '';
    const i = base.lastIndexOf('.');
    return i > 0 ? base.slice(i + 1).toLowerCase() : '';
}
function hasSep(p) {
    return /[\\/]/.test(p);
}
/** Hex runs that are plausibly commits: mixed letters+digits (or context says so). */
function shaLike(s) {
    return /[a-f]/.test(s) && /\d/.test(s);
}
/**
 * Scan prose / tool output. Overlaps resolve in favour of the earliest, longest match; URLs win
 * over the paths inside them.
 */
export function detectRefs(text) {
    if (text.length < 3)
        return [];
    const out = [];
    const taken = [];
    const free = (s, e) => !taken.some(([a, b]) => s < b && e > a);
    const add = (start, end, ref) => {
        if (!free(start, end))
            return;
        taken.push([start, end]);
        out.push({ start, end, ref });
    };
    if (text.includes('://')) {
        for (const m of text.matchAll(URL_RE)) {
            const url = trimUrl(m[0]);
            const start = m.index ?? 0;
            // Every URL is claimed (so its path is not re-detected); only local ones are refs.
            taken.push([start, start + url.length]);
            if (isLocalUrl(url))
                out.push({
                    start,
                    end: start + url.length,
                    ref: { kind: 'url', text: url, strength: 'strong' },
                });
        }
    }
    if (text.includes('_')) {
        for (const m of text.matchAll(ARTIFACT_RE)) {
            const start = m.index ?? 0;
            add(start, start + m[0].length, { kind: 'artifact', text: m[0], strength: 'strong' });
        }
    }
    if (text.includes('.')) {
        for (const m of text.matchAll(FILE_RE)) {
            const p = m[1];
            const start = m.index ?? 0;
            const sep = hasSep(p);
            const ext = extOf(p);
            if (!sep && !KNOWN_EXT.has(ext))
                continue;
            // "e.g./i.e." and domain-looking names without a directory are not files.
            if (!sep && /^(?:www\.|[\w-]+\.(?:com|org|net|io|dev|app|sh|ai)$)/i.test(p))
                continue;
            add(start, start + m[0].length, {
                kind: 'path',
                text: p,
                ...lineOf(m),
                strength: sep || m[2] || m[4] ? 'strong' : 'weak',
            });
        }
    }
    for (const m of text.matchAll(SHA_RE)) {
        const s = m[0];
        const start = m.index ?? 0;
        if (!shaLike(s) && s.length < 12)
            continue;
        const before = text.slice(Math.max(0, start - 24), start);
        // `abc1234 feat: …` (git log --oneline) or an explicit commit word just before it.
        const oneline = /(^|\n)\s*$/.test(before) &&
            /^\s+\w+(\(.+?\))?!?:/.test(text.slice(start + s.length, start + s.length + 40));
        if (!SHA_CONTEXT.test(before) && !oneline)
            continue;
        add(start, start + s.length, { kind: 'commit', text: s, strength: 'strong' });
    }
    return out.sort((a, b) => a.start - b.start);
}
/** Inline code: the whole span is one reference (path, directory, sha, branch) or none. */
export function refFromCode(code, o = {}) {
    const c = code.trim();
    if (!c || c.length > 400 || (!o.spaces && /\s/.test(c)) || /[\n\t]/.test(c))
        return null;
    if (/^https?:\/\//.test(c))
        return isLocalUrl(c) ? { kind: 'url', text: c, strength: 'strong' } : null;
    if (/^[0-9a-f]{7,40}$/.test(c))
        return shaLike(c) || c.length >= 12 ? { kind: 'commit', text: c, strength: 'strong' } : null;
    if (/^(?:main|master|develop|trunk)$/.test(c))
        return { kind: 'branch', text: c, strength: 'weak' };
    if (/^(?:art|blob)_[0-9a-f-]{36}$/.test(c))
        return { kind: 'artifact', text: c, strength: 'strong' };
    const seg = o.spaces ? SEG.replaceAll('\\]', '\\] ') : SEG;
    const m = new RegExp(String.raw `^(${PREFIX}(?:${seg}[\\/])*${seg}[\\/]?)${LINE}$`).exec(c);
    if (!m)
        return null;
    const p = m[1];
    const sep = hasSep(p.replace(/[\\/]$/, '')) || /[\\/]$/.test(p);
    const ext = extOf(p);
    if (!sep) {
        // A bare `name.ext` in code is a file mention; a bare word is not.
        if (!KNOWN_EXT.has(ext))
            return null;
        return { kind: 'path', text: p, ...lineOf(m), strength: 'strong' };
    }
    // `feature/x` style: could be a branch or a directory; the machine decides.
    if (BRANCH_PREFIX.test(p) && !ext)
        return { kind: 'path', text: p, strength: 'strong' };
    // Scoped packages (`@viko/protocol`) and slash-y prose (`and/or`) are not paths.
    if (/^@/.test(p) || (/^[a-z]+\/[a-z]+$/i.test(p) && !ext && !/[\\/]$/.test(p) && p.length < 8))
        return null;
    return { kind: 'path', text: p, ...lineOf(m), strength: 'strong' };
}
/** Markdown link targets: relative/absolute paths (not web URLs) and local dev-server URLs. */
export function refFromHref(href) {
    const h = href.trim();
    if (/^https?:\/\//i.test(h))
        return isLocalUrl(h) ? { kind: 'url', text: h, strength: 'strong' } : null;
    if (/^[a-z][a-z0-9+.-]*:/i.test(h) && !/^[A-Za-z]:[\\/]/.test(h))
        return null;
    if (h.startsWith('#') || h.startsWith('//'))
        return null;
    let decoded = h;
    try {
        decoded = decodeURI(h);
    }
    catch { }
    return refFromCode(decoded.replace(/^file:\/\//, ''), { spaces: true });
}
/** Split text into plain strings and reference spans, for renderers. */
export function segmentRefs(text, spans = detectRefs(text)) {
    if (!spans.length)
        return [text];
    const out = [];
    let i = 0;
    for (const s of spans) {
        if (s.start > i)
            out.push(text.slice(i, s.start));
        out.push(s);
        i = s.end;
    }
    if (i < text.length)
        out.push(text.slice(i));
    return out;
}
//# sourceMappingURL=refs.js.map